import React, { FC, useEffect, useRef, useState } from 'react';
import translate from '@translate';
import { formatNumber } from 'src/utils/formatNumber';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import {
    activateProductsFetch,
    deactivateProductsFetch,
    getProductsListFetch,
    syncEansToDcixFetch,
    syncProductsToDcixFetch
} from 'src/resources/productsList/reducer';
import { RootStoreType } from 'src/types/store';
import { InfoIcon, InventoryIcon, TransportIcon } from 'src/components/SkladonIcon/Icons';
import TableSimple from 'src/components/TableSimple';
import { ProductListType } from 'src/types/product';
import { CustomRenderType, ExportTypeType, SelectRowType, TableColumnType, TableRefType } from 'src/types/table';
import { useCheckPermission } from 'src/utils/checkPermission';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { UserSettingsType } from 'src/types/userSettings';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { createEanLabelUrl, getActiveEansFromProduct } from 'src/utils/createEanLink';
import { TableLink, TableRouterLink } from 'src/components/TableLink';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from 'src/components/Tooltip';
import { colors } from 'src/themes/default';
import MassEditDialog, { MassEditDialogPropsType } from './MassEditDialog';
import { patchMultipleProductsFetch } from '../../resources/productsUpdate/reducer';
import { getAllColumns } from './constants';
import { countryOptionsCodes } from '../../utils/countryOptions';
import { getProductSourceSysSelectOptions } from '../../utils/productSourceSysSelectOptions';

const InfoIconStyled = styled('span')({
    marginLeft: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    '& svg': {
        width: '14px',
        height: '14px',
    }
});

const ProductsListContainer: FC<{ filter: string }> = (props) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const tableRef = useRef<TableRefType>();

    const loading = useSelector((state: RootStoreType) => state.productsList.loading);
    const productsData = useSelector((state: RootStoreType) => state.productsList.data);
    const allStock = useSelector((state: RootStoreType) => state.productsList.allStock);
    const productInsetTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productInsetTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const expeditionMethodCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const insertByOrderTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.insertByOrderTypeCodes);
    const batteryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.batteryTypeCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const stockRation = useSelector((state: RootStoreType) => state.provider.data?.SHOPTET_STOCK_RATIO);
    const customsWarehouseEnabled = useSelector((state: RootStoreType) => state.provider.data?.CUSTOMS_WAREHOUSE_ENABLED);

    const permissionCreate = useCheckPermission('PRODUCT.CREATE');
    const permissionDetail = useCheckPermission('PRODUCT.DETAIL');
    const permissionUpdate = useCheckPermission('PRODUCT.UPDATE');
    const permissionAllProviders = useCheckPermission('PRODUCT.LISTALLPROVIDERS');
    const permissionDcixImportProducts = useCheckPermission('PRODUCT.ACTION.DCIX.IMPORT.PRODUCTS');
    const permissionDcixImportEans = useCheckPermission('PRODUCT.ACTION.DCIX.IMPORT.EANS');
    const permissionGlobalProduct = useCheckPermission('PRODUCT.GLOBAL_PRODUCT');
    const permissionQuantityConversion = useCheckPermission('PRODUCT.QUANTITY_CONVERSION');
    const [filteredProducts, setFilteredProducts] = useState<ProductListType[]>([]);
    const [filteredAllStockProducts, setFilteredAllStockProducts] = useState<ProductListType[] | null>(null);
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [massEditParams, setMassEditParams] = useState<Partial<MassEditDialogPropsType> | null>(null);
    const allColumns = getAllColumns(allStock);
    const [filteredColumns, setFilteredColumns] = useState<TableColumnType[]>(allColumns);
    const hasNavigated = sessionStorage.getItem('hasNavigated');

    useEffect(() => {

        if (location.pathname.includes('withStock') && hasNavigated) {
            // Přesměruj na novou komponentu
            navigate(location.pathname.replace('/withStock', ''), { replace: true });
             // Označ jako přesměrováno
        }

        return () => {
            // Při odchodu nebo změně cesty příznak smaž
            sessionStorage.removeItem('hasNavigated');
        };
    }, [location.pathname, navigate]);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('products.title') }));
        if (!hasNavigated) {
            dispatch(getProductsListFetch({ allStock }));
            sessionStorage.setItem('hasNavigated', 'true');
        }
    }, []);

    const reloadProductsList = () => {
        dispatch(getProductsListFetch({ allStock }));
    };

    useEffect(() => {
        if (productsData) {
            switch (props.filter) {
                case 'all':
                    saveProduct(formatProducts(productsData));
                    break;
                case 'packagingMaterial':
                    saveProduct(
                        formatProducts(
                            productsData.filter(
                                (item) =>
                                    item.PRODUCT_TYPE === 'packagingMaterial' || item.PRODUCT_TYPE === 'pallet'
                            )
                        )
                    );
                    break;
                case 'promoMaterial':
                    saveProduct(formatProducts(productsData.filter((item) => item.PRODUCT_TYPE === 'promoMaterial')));
                    break;
                case 'sets':
                    saveProduct(
                        formatProducts(productsData.filter((item) => item.PRODUCT_TYPE === 'pack'))
                    );
                    break;
                case 'bill_of_materials':
                    saveProduct(
                        formatProducts(productsData.filter((item) => item.PRODUCT_TYPE === 'bill_of_materials'))
                    );
                    break;
                case 'deleted':
                    saveProduct(
                        formatProducts(
                            productsData.filter(
                                (item) =>
                                    item.IS_DELETED === 1 &&
                                    ['packagingMaterial', 'promoMaterial', 'pallet'].indexOf(item.PRODUCT_TYPE) < 0
                            )
                        )
                    );
                    break;
                case 'inactive':
                    saveProduct(
                        formatProducts(
                            productsData.filter(
                                (item) =>
                                    item.ACTIVE === 0 &&
                                    item.IS_DELETED === 0 &&
                                    ['packagingMaterial', 'promoMaterial', 'pallet'].indexOf(item.PRODUCT_TYPE) < 0
                            )
                        )
                    );
                    break;
                case 'active':
                default:
                    saveProduct(
                        formatProducts(
                            productsData.filter(
                                (item) =>
                                    item.ACTIVE === 1 &&
                                    item.IS_DELETED === 0 &&
                                    ['packagingMaterial', 'promoMaterial', 'pallet'].indexOf(item.PRODUCT_TYPE) < 0
                            )
                        )
                    );
                    break;
            }
        }
    }, [productsData, props.filter]);

    const saveProduct = (products: ProductListType[]) => {
        const hideColumns: string[] = [];
        if (!permissionGlobalProduct) {
            hideColumns.push('GLOBAL_PRODUCT');
        }
        if (!permissionQuantityConversion) {
            hideColumns.push('QUANTITY_CONVERSION');
        }
        if (!allStock) {
            hideColumns.push('lot', 'expirationDate');
        }
        if (!customsWarehouseEnabled) {
            hideColumns.push('DUTY_QUANTITY');
        }
        const columns = allColumns.filter((item) => !hideColumns.includes(item.key));
        setFilteredColumns(columns);
        if (allStock) {
            setFilteredAllStockProducts(products);
        } else {
            setFilteredProducts(products);
            setFilteredAllStockProducts(null);
        }
    };

    const formatProducts = (products: ProductListType[]) => {
        let allRows = products;
        if (allStock) {
            allRows = [];
            products.forEach((product) => {
                if (!product.stockItems) {
                    allRows.push({ ...product, stockID: product.ID });
                } else {
                    product.stockItems.forEach((stockItem) => {
                        allRows.push({
                            ...product,
                            lot: stockItem.lot,
                            expirationDate: stockItem.expirationDate,
                            QUANTITY: stockItem.quantity,
                            EXPECTED_QUANTITY: stockItem.referenceN1,
                            QUALITY_HOLD_QUANTITY: stockItem.qualityHoldQuantity,
                            DUTY_QUANTITY: stockItem.referenceN4 ?? 0,
                            ALLOCATED_QUANTITY: stockItem.referenceN2,
                            HOLD_QUANTITY: stockItem.holdQuantity,
                            AVAILABLE_QUANTITY: Math.round(stockItem.referenceN3 * (stockRation || 1)),
                            stockID: `s${product.ID}.${stockItem.id}`
                        });
                    });
                }
            });
        }
        return allRows.map((product) => ({
            ...product,
            ALLOCATED_QUANTITY: product.ALLOCATED_QUANTITY || 0,
            HOLD_QUANTITY: product.HOLD_QUANTITY || 0,
            AVAILABLE_QUANTITY: product.AVAILABLE_QUANTITY || 0,
            QUALITY_HOLD_QUANTITY: product.QUALITY_HOLD_QUANTITY || 0,
            DUTY_QUANTITY: product.DUTY_QUANTITY || 0,
            EXPECTED_QUANTITY: product.EXPECTED_QUANTITY || 0,
            QUANTITY: product.QUANTITY || 0
        }));
    };

    const handleActivateProducts = () => {
        setConfirmParams({
            title: translate('products.activateProducts'),
            content: translate('products.activateProductsConfirm', { count: selectedRows.length }),
            action: () => {
                dispatch(activateProductsFetch(selectedRows));
                setSelectedRows([]);
            }
        });
    };
    const handleDeactivateProducts = () => {
        setConfirmParams({
            title: translate('products.deactivateProducts'),
            content: translate('products.deactivateProductsConfirm', { count: selectedRows.length }),
            action: () => {
                dispatch(deactivateProductsFetch(selectedRows));
                setSelectedRows([]);
            }
        });
    };

    const handleMassUpdate = () => {
        setMassEditParams({
            title: translate('products.massUpdate'),
            content: translate('products.massUpdateConfirm', { count: selectedRows.length }),
            action: (data) => {
                dispatch(patchMultipleProductsFetch({
                    products: productsData?.filter((product) => selectedRows.includes(String(product.ID))),
                    data
                }));
                setSelectedRows([]);
            }
        });
    };
    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(type, translate('products.action.exportFileName'));
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const toggleAllStock = () => {
        dispatch(getProductsListFetch({ allStock: !allStock }));
        setSelectedRows([]);
    };

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'NAME':
                return (
                    <TableRouterLink to={`/products/${row.ID}`}>
                        {row[column.key]}
                    </TableRouterLink>
                );
            case 'EAN': {
                const eans = getActiveEansFromProduct(row);
                if (!eans.length) {
                    return row[column.key] || '';
                }
                return (
                    <>
                        {eans.map((ean) => (
                            <div key={ean}>
                                <TableLink
                                    href={createEanLabelUrl(String(row.SKU || ''), ean)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {ean}
                                </TableLink>
                            </div>
                        ))}
                    </>
                );
            }
            case 'AVAILABLE_QUANTITY':
                if (row.AVAILABLE_QUANTITY && row.AVAILABLE_QUANTITY < 0) {
                    return <span style={{ color: colors.alert_O }}>{formatNumber(row.AVAILABLE_QUANTITY)}</span>;
                }
                break;
        }
    };

    const handleSyncProductsToDcixFetch = () => {
        dispatch(syncProductsToDcixFetch());
    };
    const handleSyncEansToDcixFetch = () => {
        dispatch(syncEansToDcixFetch());
    };

    const stockRatioPercent = Math.round((stockRation || 0) * 100);
    const stockRatioColor = stockRatioPercent === 0
        ? colors.alert_O
        : stockRatioPercent < 100
            ? colors.warning_O
            : colors.text_T2;

    return (
        <>
            <TopBlock
                reloadProductsList={reloadProductsList}
                permissionCreate={permissionCreate}
                permissionUpdate={permissionUpdate}
                permissionAllProviders={permissionAllProviders}
                permissionDcixImportProducts={permissionDcixImportProducts}
                permissionDcixImportEans={permissionDcixImportEans}
                selectedRows={selectedRows}
                filter={props.filter}
                handleActivateProducts={handleActivateProducts}
                handleDeactivateProducts={handleDeactivateProducts}
                handleExport={handleExport}
                allStock={allStock}
                toggleAllStock={toggleAllStock}
                handleSyncProductsToDcixFetch={handleSyncProductsToDcixFetch}
                handleSyncEansToDcixFetch={handleSyncEansToDcixFetch}
                handleMassUpdate={handleMassUpdate}
                productsData={productsData || []}
            />
            <TableSimple
                dataTestId="products-stock-list"
                tableRef={tableRef}
                rowIdKey={filteredAllStockProducts ? 'stockID' : 'ID'}
                selectRowIdKey="ID"
                rows={filteredAllStockProducts || filteredProducts}
                loading={loading}
                columsLabelPrefix="products.table"
                allColumns={filteredColumns}
                initSort={{ column: 'SKU', direction: 'DESC' }}
                rowActions={[
                    {
                        icon: <InventoryIcon />,
                        label: translate('products.action.productDetail'),
                        getLink: (row) => `/products/${row.ID}/`,
                        hiddenFce: () => !permissionDetail
                    },
                    // {
                    //     icon: <TransportIcon />,
                    //     label: translate('products.action.stockTransfers'),
                    //     getLink: (row) => `/products/stockTransfers/${row.ID}/`,
                    //     hiddenFce: () => !permissionDetail
                    // }
                ]}
                selectOptions={{
                    productInsetTypeCodes,
                    boolNumberCodes,
                    scanImeiSnCodes,
                    productTypeCodes,
                    expeditionMethodCodes,
                    currencyCodes,
                    countryOptionsCodes,
                    insertByOrderTypeCodes,
                    batteryTypeCodes,
                    productSourceSysCodes: getProductSourceSysSelectOptions(),
                }}
                rowSxFce={(row) => {
                    if (row.IS_DELETED === 1) {
                        return { color: colors.alert_O, '& .MuiTableCell-root': { color: colors.alert_O } };
                    }
                    if (row.ACTIVE === 0) {
                        return { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } };
                    }
                    return {};
                }}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                heightVariant="top-tab"
                fullHeight
                optionsSessionName="productsTable"
                userSettingsName="showProductsTableColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                customRender={customRender}
                footerLeft={
                    <>
                        <Typography variant="body2" sx={{ color: stockRatioColor }}>
                            {translate('products.stockRatio.label')}: {stockRatioPercent}%
                        </Typography>
                        <Tooltip title={translate('products.stockRatio.tooltip')}>
                            <InfoIconStyled>
                                <InfoIcon />
                            </InfoIconStyled>
                        </Tooltip>
                    </>
                }
            />
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
            {massEditParams && (
                <MassEditDialog
                    title={massEditParams.title || ''}
                    content={massEditParams.content}
                    action={massEditParams.action}
                    onClose={(): any => setMassEditParams(null)}
                    productTypeCodes={productTypeCodes}
                    expeditionMethodCodes={expeditionMethodCodes}
                    boolNumberCodes={boolNumberCodes}
                    scanImeiSnCodes={scanImeiSnCodes}
                    currencyCodes={currencyCodes}
                    batteryTypeCodes={batteryTypeCodes}
                />
            )}
        </>
    );
};

export default ProductsListContainer;
